/* =============================================================
   Loon'r — Cart upsells: liner (one-click add) + sock offer (pick 1 to 3)
   -------------------------------------------------------------
   Pairs with snippets/loonr-cart-liner-upsell.liquid and
   snippets/loonr-cart-sock-upsell.liquid, which render on BOTH cart
   surfaces (the AJAX side cart and the full /cart page). That markup
   re-renders on every drawer refresh, so we bind ONCE here via event
   delegation on document — the handlers keep working across re-renders
   without rebinding, and all state lives in the DOM (radio inputs).

   Flow: click [Add] -> POST /cart/add.js (size-matched liner variant,
   + a "Ships" line-item property when the snippet marks it pre-order) ->
   update the cart surface IN PLACE (Round 5 P2-2):
     - drawer: refill .previewCart-wrapper from ?view=ajax_side_cart (the
       house pattern, same as theme.js updateSidebarCart);
     - /cart page: Section Rendering API (?section_id=<main cart section>),
       swapping the line list, the totals, the add-on cards and the Closer
       (the parts theme.js updateCart swaps, plus ours). No page reload.
   Then focus moves to the new line (the socks or the liners), and a polite
   live region that stays in the page ([data-loonr-cart-status], one per
   surface) says what was added. While the request runs the button is
   aria-disabled, not disabled, so keyboard focus stays on it.

   No localStorage/sessionStorage (house rule). No ES2020+ syntax: this must
   parse on old iOS Safari (see the `?.` incident, PR #506).
   ============================================================= */
(function () {
  "use strict";

  var DRAWER_ID = "halo-cart-sidebar";
  var WRAPPER_SELECTOR = "#" + DRAWER_ID + " .halo-sidebar-wrapper .previewCart-wrapper";
  var UPSELLS_SELECTOR = "[data-loonr-cart-upsells]";
  var SOCK_PATH = "/products/offbeat-crew";
  var LINER_PATH = "/products/replacement-liner";
  var selfDispatch = false; // true while we fire our own cart-update

  function routeRoot() {
    var r = (window.routes && window.routes.root) || "";
    return r === "/" ? "" : r;
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  function getCart() {
    return fetch(routeRoot() + "/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (res) { return res.json(); });
  }

  // jQuery's html() runs the inline <script> blocks the cart markup carries
  // (window.cart_selling_array_*, read by theme.js on quantity changes), exactly
  // as theme.js does when it refreshes a cart surface. innerHTML would not.
  function setHTML(el, html) {
    if (window.jQuery) {
      window.jQuery(el).html(html);
      return;
    }
    el.innerHTML = html;
    Array.prototype.forEach.call(el.querySelectorAll("script:not([src])"), function (old) {
      var s = document.createElement("script");
      s.text = old.textContent;
      old.parentNode.replaceChild(s, old);
    });
  }

  // The badge's screen-reader label ("4 items") sits beside the visible number and
  // nothing refreshed it: after an in-place add the cart link read "Cart 4 1 item"
  // (Round 5 verify). Keep it in step with the number, whoever changes the number
  // (this file or theme.js), via syncCount and the observer at the bottom.
  function syncCountLabel(el) {
    var bubble = el.closest ? el.closest(".cart-count-bubble") : null;
    var label = bubble && bubble.querySelector(".visually-hidden");
    if (!label) return;
    var n = parseInt(el.textContent, 10);
    if (isNaN(n)) return;
    var strings = window.cartStrings || {};
    label.textContent = n + " " + (n === 1 ? (strings.item || "item") : (strings.items || "items"));
  }

  // Header bubble + the drawer's "N items" label live outside the refreshed markup.
  function syncCount(cart) {
    if (!cart || typeof cart.item_count !== "number") return;
    var strings = window.cartStrings || {};
    var countText = cart.item_count >= 100 && strings.item_99 ? strings.item_99 : String(cart.item_count);
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = countText;
      syncCountLabel(el);
    });
    var label = cart.item_count === 1 ? strings.item : strings.items;
    if (label) {
      document.querySelectorAll("[data-cart-text]").forEach(function (el) {
        el.textContent = label;
      });
    }
  }

  // Let the free-shipping bar, the ship-note flip (main-cart.liquid) and the
  // spare-liner popup's cart cache recompute (house event).
  function dispatchCartUpdate(cart) {
    selfDispatch = true;
    try {
      document.dispatchEvent(new CustomEvent("cart-update", { detail: cart || {} }));
    } finally {
      selfDispatch = false;
    }
  }

  // ── Drawer: refill the drawer body from the server-rendered side cart ──
  function refreshDrawer(cart) {
    var wrapper = document.querySelector(WRAPPER_SELECTOR);
    if (!wrapper) return Promise.resolve(false);
    return fetch(routeRoot() + "/cart?view=ajax_side_cart", { credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.text();
      })
      .then(function (html) {
        setHTML(wrapper, html);
        syncCount(cart);
        dispatchCartUpdate(cart);
        return true;
      });
  }

  // ── /cart page: Section Rendering API, swap the parts that change ──
  function cartSectionId() {
    var main = document.getElementById("main-cart-items");
    return main ? main.getAttribute("data-id") : null;
  }

  function fetchCartSection() {
    var id = cartSectionId();
    if (!id) return Promise.reject(new Error("no cart section"));
    return fetch(routeRoot() + "/cart?section_id=" + encodeURIComponent(id), { credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, "text/html");
      });
  }

  // The Loon'r parts of the summary that theme.js never re-renders: the add-on cards and the
  // Cold-Feet Closer (main-cart.liquid marks each wrapper data-loonr-cart-part="...").
  function swapParts(doc) {
    Array.prototype.forEach.call(document.querySelectorAll("[data-loonr-cart-part]"), function (current) {
      var name = current.getAttribute("data-loonr-cart-part");
      var fresh = doc.querySelector('[data-loonr-cart-part="' + name + '"]');
      if (fresh) setHTML(current, fresh.innerHTML);
    });
  }

  function refreshCartPage(cart) {
    return fetchCartSection().then(function (doc) {
      var fresh = doc.querySelector("[data-cart-content] .cart");
      var current = document.querySelector("[data-cart-content] .cart");
      if (!fresh || !current) throw new Error("cart markup missing");
      setHTML(current, fresh.innerHTML);

      // Totals: the list rows and the phone sticky bar's total (same class, matched in order).
      [".cart-total-subtotal", ".cart-total-grandtotal"].forEach(function (sel) {
        var now = document.querySelectorAll("[data-cart-total] " + sel);
        var next = doc.querySelectorAll("[data-cart-total] " + sel);
        for (var i = 0; i < now.length && i < next.length; i++) now[i].innerHTML = next[i].innerHTML;
      });
      // The savings row carries its own display:none when there is no discount, so swap it whole.
      var savingsNow = document.querySelector("[data-cart-total] .cart-total-savings");
      var savingsNext = doc.querySelector("[data-cart-total] .cart-total-savings");
      if (savingsNow && savingsNext) savingsNow.outerHTML = savingsNext.outerHTML;

      swapParts(doc);
      syncCount(cart);
      dispatchCartUpdate(cart);
      return true;
    });
  }

  // ── Focus + announcement ──
  function statusRegion(inDrawer) {
    if (inDrawer) return document.querySelector("#" + DRAWER_ID + " [data-loonr-cart-status]");
    return document.getElementById("loonr-cart-status") || document.querySelector("[data-loonr-cart-status]");
  }

  // Clear first, then write on the next tick, so saying the same thing twice is still announced.
  function announce(inDrawer, text) {
    var region = statusRegion(inDrawer);
    if (!region) return;
    region.textContent = "";
    window.setTimeout(function () { region.textContent = text; }, 120);
  }

  // The new line's product link, on whichever surface we're on.
  function findLineLink(inDrawer, path) {
    var scope = inDrawer ? document.getElementById(DRAWER_ID) : document.querySelector("[data-cart-content]");
    if (!scope) return null;
    var sel = inDrawer ? "a.previewCartItem-name" : ".cart-item-name a";
    var links = scope.querySelectorAll(sel);
    for (var i = 0; i < links.length; i++) {
      if ((links[i].getAttribute("href") || "").indexOf(path) > -1) return links[i];
    }
    return null;
  }

  function focusResult(inDrawer, path) {
    var target = findLineLink(inDrawer, path) ||
      document.getElementById(inDrawer ? "cart-sidebar-checkout" : "cart-checkout");
    if (!target) return;
    try {
      target.scrollIntoView({ block: "center", behavior: reducedMotion() ? "auto" : "smooth" });
    } catch (e) {
      target.scrollIntoView();
    }
    try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
  }

  function showError(button, message, inDrawer) {
    var container = button.closest("[data-liner-upsell]");
    var msg = container && container.querySelector("[data-liner-upsell-msg]");
    if (msg) {
      msg.textContent = message;
      msg.hidden = false;
    }
    announce(inDrawer, message);
  }

  function addLiner(button) {
    var variantId = button.getAttribute("data-variant-id");
    if (!variantId || button.getAttribute("aria-busy") === "true") return;

    var item = { id: variantId, quantity: 1 };
    // Pre-order upsell line: attach the "Ships" line-item property (NOT a selling
    // plan) so it adds as a normal purchase and discount codes apply. Mirrors the
    // PDP after the pre-order selling plan was dropped. data-ships lets each
    // snippet set its own window (liners from snippets/loonr-liner-ship, socks "mid-Oct").
    if (button.getAttribute("data-preorder") === "true") {
      item.properties = { Ships: button.getAttribute("data-ships") || "late Nov" };
    }

    postItems(button, [item], { path: LINER_PATH, message: "Added spare liners to your cart." });
  }

  // ── Sock offer (snippets/loonr-cart-sock-upsell.liquid) ─────────────────
  // Three price tiles (1 / 2 / 3 pairs) and one colorway picker per pair.
  // The tile picks how many pair rows are live; each live row contributes its
  // checked colorway. Same colorway twice is fine: quantities are summed per
  // variant so the cart gets one line per colorway.
  function sockOfferPairs(offer) {
    var tier = offer.querySelector("[data-sock-tier]:checked");
    var n = tier ? parseInt(tier.value, 10) : 1;
    return n >= 1 && n <= 3 ? n : 1;
  }

  function syncSockOffer(offer) {
    var n = sockOfferPairs(offer);
    offer.querySelectorAll("[data-sock-pair]").forEach(function (row) {
      row.hidden = parseInt(row.getAttribute("data-sock-pair"), 10) > n;
      var pick = row.querySelector("[data-sock-pick]:checked");
      var name = row.querySelector("[data-sock-pair-name]");
      if (pick && name) name.textContent = pick.getAttribute("data-colorway") || "";
    });
    var tier = offer.querySelector("[data-sock-tier]:checked");
    var button = offer.querySelector("[data-sock-offer-add]");
    if (tier && button && button.getAttribute("aria-busy") !== "true") {
      button.textContent = tier.getAttribute("data-label") || button.textContent;
    }
  }

  function addSockOffer(button) {
    var offer = button.closest("[data-sock-offer]");
    if (!offer || button.getAttribute("aria-busy") === "true") return;

    var n = sockOfferPairs(offer);
    var byVariant = {};
    var order = [];
    var pairs = 0;
    for (var slot = 1; slot <= n; slot++) {
      var pick = offer.querySelector('[data-sock-pair="' + slot + '"] [data-sock-pick]:checked');
      if (!pick || pick.disabled) continue;
      if (!byVariant[pick.value]) {
        byVariant[pick.value] = { id: pick.value, quantity: 0 };
        if (pick.getAttribute("data-preorder") === "true") {
          byVariant[pick.value].properties = { Ships: pick.getAttribute("data-ships") || button.getAttribute("data-ships") || "as soon as it lands" };
        }
        order.push(pick.value);
      }
      byVariant[pick.value].quantity += 1;
      pairs += 1;
    }
    if (!order.length) {
      showError(button, "Pick a colorway first.", !!button.closest("#" + DRAWER_ID));
      return;
    }
    postItems(button, order.map(function (id) { return byVariant[id]; }), {
      path: SOCK_PATH,
      message: pairs === 1 ? "Added 1 pair of socks to your cart." : "Added " + pairs + " pairs of socks to your cart."
    });
  }

  // Shared add: POST the items in ONE request, then update whichever cart
  // surface we are on in place, move focus to the new line and announce it.
  function postItems(button, items, result) {
    // Decide the surface now: the refresh below replaces the card, and a
    // detached button can no longer tell which surface it was on.
    var inDrawer = !!button.closest("#" + DRAWER_ID);
    var onCartPage = !inDrawer && !!document.querySelector(UPSELLS_SELECTOR) && !!cartSectionId();
    var originalLabel = button.textContent;
    button.setAttribute("aria-busy", "true");
    button.setAttribute("aria-disabled", "true");
    button.textContent = "Adding...";

    var container = button.closest("[data-liner-upsell]");
    var msg = container && container.querySelector("[data-liner-upsell-msg]");
    if (msg) { msg.hidden = true; msg.textContent = ""; }

    fetch(routeRoot() + "/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) { throw err; });
        }
        return res.json();
      })
      .then(
        function () {
          // Transitional: the refresh below removes this card (the add-on is in the cart now).
          button.textContent = button.getAttribute("data-added-text") || "Added";
          return getCart()
            .catch(function () { return null; })
            .then(function (cart) {
              if (inDrawer) return refreshDrawer(cart);
              if (onCartPage) return refreshCartPage(cart);
              return false;
            })
            .then(
              function (refreshed) {
                if (!refreshed && !inDrawer) {
                  // Not a surface we know how to refresh in place: fall back to a reload.
                  window.location.reload();
                  return;
                }
                focusResult(inDrawer, result.path);
                announce(inDrawer, result.message);
              },
              function () {
                // The add worked but the in-place refresh didn't. On /cart, reload as before.
                // In the drawer the card stays busy ("Added") so a second tap can't add it
                // twice; the next time the drawer opens it re-renders.
                if (!inDrawer) {
                  window.location.reload();
                  return;
                }
                announce(inDrawer, result.message);
              }
            );
        },
        function (err) {
          button.setAttribute("aria-busy", "false");
          button.removeAttribute("aria-disabled");
          button.textContent = originalLabel;
          var desc = (err && (err.description || err.message)) || "Couldn't add that one. Give it another go?";
          showError(button, desc, inDrawer);
        }
      );
  }

  // /cart page: theme.js updateCart (quantity change, removal) rewrites the lines
  // and totals but never the add-on cards or the Closer, so removing the boot left
  // the sock card quoting a with-boots price the checkout no longer gives. Re-render
  // just those parts after it. Our own refreshes already did, so skip those.
  // Liner-led cart test (snippets/loonr-liner-test-arm.liquid): copy this cart's arm (A or B) from the marker the
  // cart markup carries to the cart attribute _liner_test, so it lands on the order for the read. Writes only when
  // the cart doesn't carry that arm yet; the arm is a hash of the cart token, so a stale marker is still right.
  var armSent = "";
  function syncTestArm() {
    var marker = document.querySelector("[data-liner-test]");
    if (!marker) return;
    var arm = marker.getAttribute("data-liner-test");
    if (!arm || arm === marker.getAttribute("data-liner-test-current") || arm === armSent) return;
    armSent = arm;
    fetch(routeRoot() + "/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { _liner_test: arm } })
    }).then(function (res) {
      if (!res.ok) armSent = "";
    }).catch(function () { armSent = ""; });
  }

  var partsTimer = null;
  function onCartUpdate() {
    syncTestArm();
    if (selfDispatch) return;
    if (!document.querySelector("[data-loonr-cart-part]") || !cartSectionId()) return;
    if (document.getElementById(DRAWER_ID)) return; // drawer pages re-render the whole drawer
    window.clearTimeout(partsTimer);
    partsTimer = window.setTimeout(function () {
      fetchCartSection().then(swapParts).catch(function () { /* they refresh on the next load */ });
    }, 150);
  }

  // Delegated: one binding covers every re-rendered instance of the upsell
  // (drawer AND the full cart page). Bind exactly once — a second binding
  // would add two liners per click if this script ever loaded twice.
  if (!window.__loonrLinerUpsellBound) {
    window.__loonrLinerUpsellBound = true;
    document.addEventListener("click", function (event) {
      if (!event.target.closest) return;
      var sockButton = event.target.closest("[data-sock-offer-add]");
      if (sockButton) {
        event.preventDefault();
        addSockOffer(sockButton);
        return;
      }
      var button = event.target.closest("[data-liner-upsell-add]");
      if (!button) return;
      event.preventDefault();
      addLiner(button);
    });
    // Tier tiles and colorway swatches are native radios: keyboard and screen
    // readers work for free, we only mirror the choice into the card.
    document.addEventListener("change", function (event) {
      var target = event.target;
      if (!target || !target.matches || !target.matches("[data-sock-tier], [data-sock-pick]")) return;
      var offer = target.closest("[data-sock-offer]");
      if (offer) syncSockOffer(offer);
    });
    document.addEventListener("cart-update", onCartUpdate);
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", syncTestArm);
    } else {
      syncTestArm();
    }

    // Removing a line (theme.js [data-cart-remove], both surfaces) re-renders the cart and
    // dropped keyboard focus to <body> (Round 5 verify). Note where the removed line sat,
    // then wait until the cart markup stops changing (the drawer renders twice, 200-500ms
    // apart; focusing after the first render lands on a node the second one replaces) and
    // focus the line that took its place (or the new last one), else the drawer's close
    // button / the page's main area, and say what happened. Capture phase, so we see the
    // click even if theme.js stops it; we never prevent it.
    document.addEventListener("click", function (event) {
      var btn = event.target.closest ? event.target.closest("[data-cart-remove]") : null;
      if (!btn || !window.MutationObserver) return;
      var inDrawer = !!btn.closest("#" + DRAWER_ID);
      var before = lineLinks(inDrawer);
      var row = btn.closest("[data-item-row]");
      var rowLink = row ? row.querySelector(inDrawer ? "a.previewCartItem-name" : ".cart-item-name a") : null;
      var index = Math.max(0, before.indexOf(rowLink));
      var root = inDrawer ? document.getElementById(DRAWER_ID) : cartSectionRoot();
      if (!root) return;
      var settle = null;
      var observer = new MutationObserver(function () {
        window.clearTimeout(settle);
        settle = window.setTimeout(finish, 300);
      });
      var giveUp = window.setTimeout(function () { observer.disconnect(); }, 6000);
      observer.observe(root, { childList: true, subtree: true });
      function finish() {
        var links = lineLinks(inDrawer);
        var changed = links.length !== before.length || (links[0] && before[0] && !document.contains(before[0]));
        if (!changed) return; // not re-rendered yet: keep waiting for the next quiet spell
        observer.disconnect();
        window.clearTimeout(giveUp);
        var active = document.activeElement;
        if (active && active !== document.body && document.contains(active)) return; // something else placed focus
        var target = links.length ? links[Math.min(index, links.length - 1)] : emptyCartFocus(inDrawer);
        if (target) target.focus();
        announce(inDrawer, links.length ? "Removed from your cart." : "Removed from your cart. Your cart is empty.");
      }
    }, true);
  }

  // The /cart page swaps the parts inside its section, so watch the section, not the parts.
  function cartSectionRoot() {
    var content = document.querySelector("[data-cart-content]");
    if (!content) return null;
    return content.closest(".shopify-section") || content.closest("main") || document.body;
  }

  function lineLinks(inDrawer) {
    var scope = inDrawer ? document.getElementById(DRAWER_ID) : document.querySelector("[data-cart-content]");
    if (!scope) return [];
    return Array.prototype.slice.call(scope.querySelectorAll(inDrawer ? "a.previewCartItem-name" : ".cart-item-name a"));
  }

  function emptyCartFocus(inDrawer) {
    if (inDrawer) {
      var drawer = document.getElementById(DRAWER_ID);
      return drawer ? drawer.querySelector(".halo-sidebar-close, [data-close-cart-sidebar], button") : null;
    }
    var main = document.getElementById("MainContent") || document.querySelector("main");
    if (main && !main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
    return main;
  }

  // Keep every badge's hidden label in step when theme.js rewrites the number itself.
  if (window.MutationObserver && !window.__loonrCartCountObserved) {
    window.__loonrCartCountObserved = true;
    var countObserver = new MutationObserver(function (records) {
      records.forEach(function (record) {
        var el = record.target.nodeType === 3 ? record.target.parentNode : record.target;
        if (el && el.hasAttribute && el.hasAttribute("data-cart-count")) syncCountLabel(el);
      });
    });
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      countObserver.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }
})();
